import { onlyDocumentChars } from 'shared/helpers/string';
import {
  CsvRow,
  CsvValidationError,
  CsvValidationResult,
} from '../types/nfse.types';

const CPF_REGEX = /^\d{3}\.?\d{3}\.?\d{3}-?\d{2}$/;
const CNPJ_REGEX =
  /^[A-Za-z0-9]{2}\.?[A-Za-z0-9]{3}\.?[A-Za-z0-9]{3}\/?[A-Za-z0-9]{4}-?\d{2}$/;

export function validateDocument(doc: string): 'CPF' | 'CNPJ' | null {
  const cleanDoc = onlyDocumentChars(doc);

  if (cleanDoc.length === 11 && CPF_REGEX.test(doc)) {
    return 'CPF';
  }

  if (cleanDoc.length === 14 && CNPJ_REGEX.test(doc)) {
    return 'CNPJ';
  }

  // Also check cleaned version
  if (cleanDoc.length === 11) {
    return 'CPF';
  }

  if (cleanDoc.length === 14) {
    return 'CNPJ';
  }

  return null;
}

export function cleanDocument(doc: string): string {
  return onlyDocumentChars(doc);
}

export function parseValue(value: string | number): number {
  if (typeof value === 'number') {
    return value;
  }

  // Handle Brazilian format: 1.234,56
  const cleanValue = value
    .replace(/\s/g, '')
    .replace(/R\$/g, '')
    .replace(/\./g, '')
    .replace(',', '.');

  const parsed = parseFloat(cleanValue);
  return isNaN(parsed) ? 0 : parsed;
}

export function parseCsvLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];

    if (char === '"') {
      inQuotes = !inQuotes;
    } else if ((char === ',' || char === ';') && !inQuotes) {
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }

  result.push(current.trim());
  return result;
}

export async function validateCsvSchema(
  file: File,
): Promise<CsvValidationResult> {
  const errors: CsvValidationError[] = [];
  const data: CsvRow[] = [];

  try {
    const text = await file.text();
    const lines = text.split(/\r?\n/).filter((line) => line.trim() !== '');

    if (lines.length === 0) {
      return {
        valid: false,
        errors: [{ row: 0, field: 'file', message: 'Arquivo vazio' }],
        data: [],
      };
    }

    // Check header
    const header = parseCsvLine(lines[0].toLowerCase());
    const documentIdx = header.findIndex(
      (h) =>
        h === 'document' ||
        h === 'documento' ||
        h === 'doc' ||
        h === 'cpf' ||
        h === 'cnpj',
    );
    const valueIdx = header.findIndex(
      (h) => h === 'value' || h === 'valor' || h === 'total',
    );

    if (documentIdx === -1) {
      errors.push({
        row: 1,
        field: 'header',
        message: 'Coluna "document" ou "documento" não encontrada no cabeçalho',
      });
    }

    if (valueIdx === -1) {
      errors.push({
        row: 1,
        field: 'header',
        message: 'Coluna "value" ou "valor" não encontrada no cabeçalho',
      });
    }

    if (errors.length > 0) {
      return { valid: false, errors, data: [] };
    }

    // Parse data rows
    for (let i = 1; i < lines.length; i++) {
      const columns = parseCsvLine(lines[i]);
      const rowNumber = i + 1;

      if (columns.length < Math.max(documentIdx, valueIdx) + 1) {
        errors.push({
          row: rowNumber,
          field: 'row',
          message: 'Linha com número insuficiente de colunas',
        });
        continue;
      }

      const document = columns[documentIdx];
      const value = columns[valueIdx];

      // Validate document
      const docType = validateDocument(document);
      if (!docType) {
        errors.push({
          row: rowNumber,
          field: 'document',
          message: `Documento inválido: "${document}" - deve ser CPF ou CNPJ válido`,
        });
      }

      // Validate value
      const parsedValue = parseValue(value);
      if (parsedValue <= 0) {
        errors.push({
          row: rowNumber,
          field: 'value',
          message: `Valor inválido: "${value}" - deve ser um número positivo`,
        });
      }

      if (docType && parsedValue > 0) {
        data.push({
          document: cleanDocument(document),
          value: parsedValue,
        });
      }
    }

    return {
      valid: errors.length === 0,
      errors,
      data,
    };
  } catch (error) {
    return {
      valid: false,
      errors: [
        {
          row: 0,
          field: 'file',
          message: `Erro ao processar arquivo: ${error instanceof Error ? error.message : 'Erro desconhecido'}`,
        },
      ],
      data: [],
    };
  }
}

export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
}

export function formatDocument(doc: string): string {
  const clean = onlyDocumentChars(doc);

  if (clean.length === 11) {
    return clean.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }

  if (clean.length === 14) {
    return clean.replace(
      /([A-Z0-9]{2})([A-Z0-9]{3})([A-Z0-9]{3})([A-Z0-9]{4})(\d{2})/,
      '$1.$2.$3/$4-$5',
    );
  }

  return doc;
}

export function formatDate(dateStr?: string): string {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR');
  } catch {
    return dateStr;
  }
}

export function formatDateISO(date: Date): string {
  return date.toISOString().split('T')[0];
}
